import type { FC, ReactNode } from 'react'
import type { Track } from '@/lib/tracks'

import { LoaderCircle, Pause, Play } from 'lucide-react'
import { cn } from '@/lib/utils'
import { buttonClasses } from '@/components/kit/button'
import { usePlayer } from '@/components/player/player-provider'

interface ListenButtonProps {
    track: Track
    context?: Array<Track>
    label?: string
    variant?: 'primary' | 'outline' | 'gold'
    className?: string
}

/** A labelled play button, such as "Listen now", for the main call to action on a release. */
export const ListenButton: FC<ListenButtonProps> = ({ track, context, label = 'Listen now', variant = 'primary', className }): ReactNode => {
    const { current, isPlaying, isBuffering, toggleTrack } = usePlayer()
    const active = current?.id === track.id
    const playing = active && isPlaying

    return (
        <button
            type='button'
            onClick={() => toggleTrack(track, context)}
            aria-pressed={playing}
            className={cn(buttonClasses(variant, 'lg'), 'gap-3', className)}
        >
            {active && isBuffering && isPlaying
                ? <LoaderCircle className='animate-spin' aria-hidden='true' />
                : playing
                    ? <Pause className='fill-current' aria-hidden='true' />
                    : <Play className='fill-current' aria-hidden='true' />}
            {playing ? 'Pause' : label}
        </button>
    )
}

interface PlayAllButtonProps {
    tracks: Array<Track>
    label?: string
    variant?: 'primary' | 'outline' | 'gold'
    className?: string
}

export const PlayAllButton: FC<PlayAllButtonProps> = ({ tracks, label = 'Play all', variant = 'primary', className }): ReactNode => {
    const { playTracks, queue, isPlaying, togglePlay } = usePlayer()
    const sameQueue = tracks.length > 0 && queue.length === tracks.length && queue.every((track, index) => track.id === tracks[index]?.id)

    if (tracks.length === 0) return null

    return (
        <button
            type='button'
            onClick={() => (sameQueue ? togglePlay() : playTracks(tracks, 0))}
            className={cn(buttonClasses(variant, 'lg'), 'gap-3', className)}
        >
            {sameQueue && isPlaying ? <Pause className='fill-current' aria-hidden='true' /> : <Play className='fill-current' aria-hidden='true' />}
            {sameQueue && isPlaying ? 'Pause' : label}
        </button>
    )
}
